import { useCallback } from 'react';
import { useQuery } from 'src/shared/lib/useQuery';
import { STANDARDMODELS } from 'src/shared/constants/models.constants';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { getTagHierarchy, getTagNerHierarchy, sortModels } from 'src/shared/lib/model.utils';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Model, Tag } from 'src/shared/types/model.types';
import { QueryResult } from 'src/shared/types/util.types';

export const BASE_PATH = '/api/org/facets';

export function useModels(): QueryResult<Model[]>;
export function useModels(modelId: string): QueryResult<Model>;
export function useModels(modelId: string, tagId: string): QueryResult<Tag>;
export function useModels(modelId?: string, tagId?: string): QueryResult<Model | Model[] | Tag> {
  const { user } = useAuth();
  const { orgFetch, currentOrg, role } = useOrg();

  const queryKey = ['models', genQueryKey(currentOrg.id)];

  // ✅ Memoize selector function - stable reference unless dependencies change
  // TanStack Query uses structural sharing on the result for optimal performance
  const selectModelData = useCallback(
    (models: Model[]) => {
      if (!modelId) {
        return models;
      }
      if (tagId && modelId) {
        return models.find((model) => model.id === modelId)?.tags.find((tag) => tag.internalId === tagId);
      }
      return models.find((model) => model.id === modelId);
    },
    [modelId, tagId]
  );

  return useQuery({
    queryFn: getData,
    queryKey: queryKey,
    select: selectModelData,
  });

  async function getData() {
    const res = await orgFetch<Model[]>('/api/v2/models');
    if (!res.ok) {
      throw new Error('Failed to fetch models');
    }
    const models = await res.json();
    const modelsWithTagHierarchies = models.map((model) => {
      const cleanedTags = model.tags.map((tag) => ({
        ...tag,
        shortName: tag.shortName || tag.internalId,
        hierarchy: getTagHierarchy(tag.id, model.tags),
        nerHierarchy: getTagNerHierarchy(tag.id, model.tags),
        children: tag.children?.filter((child) => model.tags.some((tag) => tag.id === child.id)) || [],
        TagExamples: tag.TagExamples || [],
      }));

      const isProcessModel = Array.isArray(model.processIds) && model.processIds.length > 0;

      return {
        ...model,
        isNer: model.settings?.modelType === 'ner',
        readonly: STANDARDMODELS.includes(model.id),
        displayName: model.displayName || model.id,
        tags: cleanedTags.sort((a, b) => a.hierarchy.join('').localeCompare(b.hierarchy.join(''))),
        bookmarked: user?.settings?.bookmarks?.includes(model.id) || false,
        isProcessModel,
      };
    });

    return sortModels(modelsWithTagHierarchies).filter((model) => role === 'SUPPORT' || !model.hidden);
  }
}
